import {useEffect, useState} from 'react';
import {useDeferredData, useProvideDeferredData} from './useDeferredData';
import useSetUI from './useSetUI';
import {registerModuleStateReset} from 'coreUtils/moduleState';

const IS_SERVER_KEY = 'is-server';

let _initialFlipDone = false;

registerModuleStateReset(() => {
	_initialFlipDone = false;
});

/**
 * Reads the current `isServer` flag from deferred data.
 * Suspense-safe alternative to `useUI('isServer')`.
 */
export function useIsServer(): boolean {
	return useDeferredData<boolean>(IS_SERVER_KEY);
}

/**
 * Provides the `isServer` value for deferred-data consumers.
 */
export function useProvideIsServer(value: boolean, options?: {isFallback?: boolean}): void {
	useProvideDeferredData(IS_SERVER_KEY, value, [], options);
}

/**
 * Fallback provider: emits `isServer = true` while the page is still
 * loading (inside a Suspense boundary). After the first page renders and
 * flips to `false`, this fallback also provides `false` to stay consistent.
 *
 * Place in the app shell (outside page-level Suspense boundaries).
 *
 * NOTE: only needed when components OUTSIDE the page Suspense boundary
 * consume `useIsServer()`. If all consumers use `useUI('isServer')` (Redux),
 * this component is not required — `IsServerResolved` alone handles the
 * Redux dispatch at the correct time.
 */
export const IsServerFallback = (): null => {
	useProvideIsServer(!_initialFlipDone, {isFallback: true});

	return null;
};

/**
 * Resolves `isServer` to `false` after page content has mounted.
 * Must live inside `withSSRPage` (rendered after `Content`) so it only
 * mounts when the page's Suspense boundary resolves.
 *
 * On SSR: provides `true` (effects don't run, stays in server state).
 * On client (first page after hydration): provides `true` initially, then
 * flips to `false` after mount (useEffect).
 * On client (CSR navigation): provides `false` immediately (no extra render).
 *
 * Also dispatches `setUI({isServer: false})` to Redux for backward compat
 * with existing `useUI('isServer')` consumers.
 */
export const IsServerResolved = (): null => {
	const uiMgr = useSetUI();
	const isSSR = typeof window === 'undefined';
	const alreadyResolved = !isSSR && _initialFlipDone;
	const [ready, setReady] = useState(alreadyResolved);

	useProvideIsServer(!ready);

	useEffect(() => {
		if (!ready) {
			setReady(true);
		}
	}, []);

	useEffect(() => {
		if (ready) {
			_initialFlipDone = true;
			uiMgr.setUI({isServer: false});
		}
	}, [ready]);

	return null;
};
